import { Form } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import { FormContent, FormFields } from "@/components/page-canvas";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { ImageInput } from "@/components/image-input";
import { Spinner } from "@/components/ui/spinner";
import { userProfilePath } from "@/routes";
import type { User } from "@/types";

export function ProfileForm({ user }: { user: User }) {
  const { t } = useTranslation();
  const initials =
    `${user.first_name?.[0] || ""}${user.last_name?.[0] || ""}`.toUpperCase();

  return (
    <Form
      action={userProfilePath()}
      method="patch"
      options={{ preserveScroll: true }}
    >
      {({ errors, processing }) => (
        <FormContent>
          <FieldGroup>
            <div className="flex flex-wrap items-center gap-4">
              <Avatar className="size-16">
                <AvatarImage src={user.avatar_url || undefined} alt="" />
                <AvatarFallback>{initials}</AvatarFallback>
              </Avatar>
              <Field className="min-w-0 flex-1 basis-48">
                <FieldLabel htmlFor="avatar">
                  {t("profile_ui.avatar")}
                </FieldLabel>
                <ImageInput
                  id="avatar"
                  name="user[avatar]"
                  accept="image/png,image/jpeg,image/gif,image/webp"
                />
                <FieldError>{errors.avatar?.[0]}</FieldError>
              </Field>
            </div>
            <FormFields>
              <Field>
                <FieldLabel htmlFor="first_name">
                  {t("onboarding.first_name")}
                </FieldLabel>
                <Input
                  id="first_name"
                  name="user[first_name]"
                  defaultValue={user.first_name}
                  required
                />
                <FieldError>{errors.first_name?.[0]}</FieldError>
              </Field>
              <Field>
                <FieldLabel htmlFor="last_name">
                  {t("onboarding.last_name")}
                </FieldLabel>
                <Input
                  id="last_name"
                  name="user[last_name]"
                  defaultValue={user.last_name}
                  required
                />
                <FieldError>{errors.last_name?.[0]}</FieldError>
              </Field>
            </FormFields>
            <Field>
              <FieldLabel htmlFor="email_address">{t("auth.email")}</FieldLabel>
              <Input
                id="email_address"
                name="user[email_address]"
                type="email"
                defaultValue={user.email_address}
                required
              />
              <FieldError>{errors.email_address?.[0]}</FieldError>
            </Field>
            <Button type="submit" className="w-fit" disabled={processing}>
              {processing && <Spinner />}
              {t("common.save")}
            </Button>
          </FieldGroup>
        </FormContent>
      )}
    </Form>
  );
}
